import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import PicImg from "../PicImg";

/**
 * Related Products — 3-card grid linking to other product pages.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "More Solutions")
 *  - heading (ReactNode)
 *  - items: { slug, name, desc, image }[]
 */
export default function RelatedProducts({
  testId,
  eyebrow = "More Solutions",
  heading,
  items = [],
}) {
  return (
    <section
      data-testid={testId}
      className="bg-liftile-cream-light py-24 md:py-32 text-liftile-matte-black"
    >
      <div className="container-x">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="max-w-2xl mb-14 md:mb-16"
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2 className="display-2">{heading}</h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7 md:gap-8">
          {items.map((r, i) => (
            <motion.article
              key={r.slug}
              data-testid={`${testId}-${r.slug}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.6,
                delay: i * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group h-full flex flex-col bg-white rounded-2xl overflow-hidden shadow-[0_4px_18px_rgba(0,0,0,0.05)] hover:shadow-[0_20px_50px_-12px_rgba(0,0,0,0.18)] border border-transparent hover:border-liftile-orange/40 hover:-translate-y-1.5 transition-all duration-500"
            >
              <Link
                to={`/products/${r.slug}`}
                className="block aspect-[4/3] overflow-hidden bg-liftile-cream-light"
              >
                <PicImg
                  src={r.image}
                  alt={r.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" loading="lazy" decoding="async" />
              </Link>
              <div className="p-6 md:p-7 flex-1 flex flex-col">
                <Link to={`/products/${r.slug}`}>
                  <h3 className="font-heading text-lg md:text-xl uppercase tracking-wider font-semibold text-liftile-matte-black leading-snug group-hover:text-liftile-orange transition-colors duration-300">
                    {r.name}
                  </h3>
                </Link>
                <p className="font-body text-sm md:text-[15px] text-liftile-matte-black/70 mt-3 leading-relaxed flex-1">
                  {r.desc}
                </p>
                <Link
                  to={`/products/${r.slug}`}
                  data-testid={`${testId}-cta-${r.slug}`}
                  className="mt-6 inline-flex items-center gap-2 font-heading text-xs uppercase tracking-[0.2em] font-semibold text-liftile-orange self-start hover:gap-3 transition-all duration-300"
                >
                  View Product <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
